import { format } from "date-fns";
import type { Locale } from "date-fns";
import type { ProductDetail, ProductOption } from "@/types";
import type { CartItem } from "@/types/booking";
import {
  getDailyRateForDays,
  calculateRentalDays,
  calculateRentalPrice,
  calculateSeasonalDailyV2Price,
  calculateSeasonalRentalPrice,
  getSeasonalTimeslotPrice,
} from "./pricing";
import { getDailyV2TiersForSub } from "./daily-matrix";
import { customerPolicyTotalFromTable } from "@/lib/pricing/base-plus-extra";
import type { SupportedCurrency } from "./currency";
import {
  perDayRentalDays,
  resolvePerDayOptionQuantity,
} from "@/lib/mobility/per-day-options";

// Re-export from pricing.ts for backward compatibility
export { calculateRentalDays, calculateRentalPrice };

/**
 * Helper to format date as YYYY-MM-DD in local timezone
 * Prevents UTC conversion issues
 */
export function formatDateToLocalISO(date: Date | undefined): string | undefined {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export interface RentalCheckoutData {
  pickupBranchId: string | null | undefined;
  pickupBranchName: string | undefined;
  pickupMethodType: string | undefined;
  isPickupPhysicalBranch: boolean;
  pickupDate: string | undefined;
  pickupTime: string | undefined;
  returnBranchId: string | null | undefined;
  returnBranchName: string | undefined;
  returnMethodType: string | undefined;
  isReturnPhysicalBranch: boolean | undefined;
  returnDate: string | undefined;
  returnTime: string | undefined;
  timeSlotId: string | undefined;
  timeSlotDuration: number | undefined;
  timeSlotLabel: string | undefined;
  /** 슬롯 정식 키 (display_order) — 재고 검증·예약 스냅샷용 */
  timeSlotDisplayOrder: number | undefined;
  /** Timeslot v2 매트릭스 좌표 (Phase B-4) — 서버 재가격·예약 스냅샷용, v1은 undefined */
  subDisplayOrder?: number;
  optionDisplayOrder?: number;
  /** v2 단가 (참고값 — 서버가 셀에서 재확정) + 표시 라벨 */
  unitPrice?: number;
  subProductName?: string;
  timeOptionLabel?: string;
  days: number;
  price: number;
  quantity: number;
  /** 성수기 재정의 요금이 반영되었는지 (결제 화면 안내용) */
  seasonalApplied: boolean;
  location: string;
  date: string;
  dateRange: string;
}

export interface OptionCheckoutData {
  id: string | undefined;
  choiceId?: string;
  name: string | undefined;
  value: string | number | undefined;
  price: number;
  quantity: number;
  // New fields for input/file option types
  optionType?: "select" | "input" | "file" | "link";
  /** 하루당 옵션(09-30) — quantity = 대여일수(서버가 다시 강제), 표시는 "단가 × N일" */
  perDay?: boolean;
  inputValue?: string;      // For input type options
  pendingFiles?: File[];    // For file type options — held in checkout state until pre-payment upload
}

export interface CheckoutData {
  product: {
    id: string;
    name: string;
    description: string;
    image: string;
    partnerId: string;
    partnerName?: string;
    /** ISO-2 country code (KR/JP). `delivery` 라벨 결정 + 결제 country mismatch 검증. */
    partnerCountryCode?: string | null;
    currency: SupportedCurrency;
  };
  rental: RentalCheckoutData | null;
  rentals: RentalCheckoutData[];
  options: OptionCheckoutData[];
  cancelable: boolean;
  // Available branches for this product
  availableBranches?: Array<{ id: string; name: string }>;
  // Allowed usage methods for this product (only enabled ones)
  usageMethods?: Array<{
    method_type: "pickup" | "delivery" | "shipping";
    description?: string;
    pickup_branch_id?: string;
    return_branch_id?: string;
  }>;
  // Input/file options with full details (for checkout page entry)
  inputFileOptions?: ProductOption[];
}

interface BuildCheckoutDataParams {
  product: ProductDetail | null;
  cartItems: CartItem[];
  pricingMode: "daily" | "timeslot" | "option_only";
  locale: string;
  dateFnsLocale: Locale;
  sourceCurrency: SupportedCurrency;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, values?: any) => string;
}

/**
 * Build checkout data from cart items
 * Unified logic for both desktop (BookingCard) and mobile (Drawer)
 */
export function buildCheckoutData({
  product,
  cartItems,
  pricingMode,
  locale,
  dateFnsLocale,
  sourceCurrency,
  t,
}: BuildCheckoutDataParams): CheckoutData | null {
  if (!product) return null;

  const rentalItems = cartItems.filter((item) => item.type === "rental");
  const optionItems = cartItems.filter((item) => item.type === "option");

  // Get shipping method for shipping branch ID lookup
  const shippingMethod = product.usage_methods?.find(
    (m) => m.method_type === "shipping" && m.enabled
  );

  const rentals: RentalCheckoutData[] = rentalItems.map((rentalItem) => {
    // Calculate days (inclusive)
    const days = calculateRentalDays(rentalItem.pickupDate, rentalItem.returnDate);

    // Calculate price (timeslot uses slot price, daily uses days * rate)
    // 성수기(pricing_seasons) 재정의 반영 — 서버 검증과 동일 함수
    // Timeslot v2 (Phase B-4): 단가 = unitPrice(셀 가격), 성수기는 (sub, option)
    // 행으로 재정의(09-21) — 서버가 셀+시즌으로 재확정
    const isTimeslotV2Item =
      rentalItem.optionDisplayOrder != null && rentalItem.unitPrice != null;
    // Daily v2 (08-17): 세부 상품 티어 단가 × 대여일수. 성수기는 (sub, tier) 행으로
    // 일별 재정의(09-21) — 타임슬롯 v2와 달리 optionDisplayOrder가 없고 days 곱이 붙는다.
    const isDailyV2Item =
      !isTimeslotV2Item &&
      rentalItem.subDisplayOrder != null &&
      rentalItem.unitPrice != null &&
      days > 0;
    const seasonalResult = isTimeslotV2Item
      ? getSeasonalTimeslotPrice(
          rentalItem.unitPrice!,
          rentalItem.optionDisplayOrder,
          rentalItem.pickupDate,
          product.pricing_seasons,
          rentalItem.subDisplayOrder ?? null
        )
      : isDailyV2Item
      ? calculateSeasonalDailyV2Price(
          rentalItem.pickupDate,
          rentalItem.returnDate,
          1,
          getDailyV2TiersForSub(product.day_prices, rentalItem.subDisplayOrder),
          rentalItem.subDisplayOrder!,
          product.pricing_seasons
        )
      : rentalItem.timeSlotPrice
      ? getSeasonalTimeslotPrice(
          rentalItem.timeSlotPrice,
          rentalItem.timeSlotDisplayOrder,
          rentalItem.pickupDate,
          product.pricing_seasons,
          null
        )
      : calculateSeasonalRentalPrice(
          rentalItem.pickupDate,
          rentalItem.returnDate,
          1,
          product.pricing || [],
          product.pricing_seasons
        );
    const policyRentalPrice = isDailyV2Item
      ? customerPolicyTotalFromTable({
          table: product.policyTable,
          subDisplayOrder: rentalItem.subDisplayOrder!,
          days,
          pickupDate: rentalItem.pickupDate,
          markedSeasons: product.pricing_seasons,
        })
      : null;
    const rentalPrice = policyRentalPrice ?? seasonalResult.total;

    // Determine method types
    const isPickupMethodType =
      rentalItem.pickupBranchId === "delivery" ||
      rentalItem.pickupBranchId === "shipping";
    const isReturnMethodType =
      rentalItem.returnBranchId === "delivery" ||
      rentalItem.returnBranchId === "shipping";

    const pickupMethodType = isPickupMethodType
      ? rentalItem.pickupBranchId
      : "pickup";

    // timeslot mode has no return info
    const returnMethodType =
      pricingMode === "timeslot"
        ? undefined
        : isReturnMethodType
        ? rentalItem.returnBranchId
        : "pickup";

    // Branch IDs: physical branch → use as-is, shipping → use shipping branch ID
    const pickupBranchId =
      rentalItem.pickupBranchId === "shipping"
        ? shippingMethod?.pickup_branch_id || null
        : isPickupMethodType
        ? null
        : rentalItem.pickupBranchId;

    const returnBranchId =
      pricingMode === "timeslot"
        ? undefined
        : rentalItem.returnBranchId === "shipping"
        ? shippingMethod?.return_branch_id || null
        : isReturnMethodType
        ? null
        : rentalItem.returnBranchId;

    // Physical branch flags for checkout UI branching
    const isPhysicalBranch =
      !!rentalItem.pickupBranchId &&
      rentalItem.pickupBranchId !== "delivery" &&
      rentalItem.pickupBranchId !== "shipping";

    const isReturnPhysicalBranch =
      !!rentalItem.returnBranchId &&
      rentalItem.returnBranchId !== "delivery" &&
      rentalItem.returnBranchId !== "shipping";

    // Format date range string
    // For shipping (no time), only show dates
    const dateRange =
      rentalItem.pickupDate && rentalItem.returnDate
        ? `${format(rentalItem.pickupDate, "M/d")}${
            rentalItem.pickupTime ? ` ${rentalItem.pickupTime}` : ""
          } - ${format(rentalItem.returnDate, "M/d")}${
            rentalItem.returnTime ? ` ${rentalItem.returnTime}` : ""
          }`
        : rentalItem.pickupDate
        ? `${format(rentalItem.pickupDate, "M/d")}${
            rentalItem.pickupTime ? ` ${rentalItem.pickupTime}` : ""
          } (${
            // Timeslot v2: 시간 계산 없음 — 옵션 라벨 표시 (v1은 기존 시간 표기)
            rentalItem.timeSlotDuration != null
              ? t("hours", { count: rentalItem.timeSlotDuration })
              : rentalItem.timeOptionLabel || rentalItem.timeSlotLabel || ""
          })`
        : "";

    return {
      pickupBranchId,
      pickupBranchName: rentalItem.pickupBranchName,
      pickupMethodType,
      isPickupPhysicalBranch: isPhysicalBranch,
      pickupDate: formatDateToLocalISO(rentalItem.pickupDate),
      pickupTime: rentalItem.pickupTime,
      returnBranchId: pricingMode === "timeslot" ? undefined : returnBranchId,
      returnBranchName:
        pricingMode === "timeslot" ? undefined : rentalItem.returnBranchName,
      returnMethodType,
      isReturnPhysicalBranch:
        pricingMode === "timeslot" ? undefined : isReturnPhysicalBranch,
      returnDate:
        pricingMode === "timeslot"
          ? undefined
          : formatDateToLocalISO(rentalItem.returnDate),
      returnTime:
        pricingMode === "timeslot" ? undefined : rentalItem.returnTime,
      timeSlotId: rentalItem.timeSlotId,
      timeSlotDuration: rentalItem.timeSlotDuration,
      timeSlotLabel: rentalItem.timeSlotLabel,
      timeSlotDisplayOrder: rentalItem.timeSlotDisplayOrder,
      // Timeslot v2 좌표 + 표시 라벨 (Phase B-4)
      subDisplayOrder: rentalItem.subDisplayOrder,
      optionDisplayOrder: rentalItem.optionDisplayOrder,
      unitPrice: rentalItem.unitPrice,
      subProductName: rentalItem.subProductName,
      timeOptionLabel: rentalItem.timeOptionLabel,
      days,
      price: rentalPrice,
      quantity: rentalItem.quantity,
      seasonalApplied: seasonalResult.seasonalApplied,
      location: rentalItem.pickupBranchName
        ? `${rentalItem.pickupBranchName} · ${t("totalItems", {
            count: rentalItem.quantity,
          })}`
        : t("totalItems", { count: rentalItem.quantity }),
      date: rentalItem.pickupDate
        ? format(rentalItem.pickupDate, "PPP (E)", { locale: dateFnsLocale })
        : "",
      dateRange,
    };
  });

  // 하루당 옵션(09-30): 수량 = 결제할 대여(rentals[0])의 일수. 서버
  // (validateDirectCheckoutPrice)가 같은 규칙으로 다시 강제하므로 여기 값은 표시·합계용.
  const perDayDays = perDayRentalDays(pricingMode, rentals[0]?.days);
  const options: OptionCheckoutData[] = optionItems.map((item) => ({
    id: item.optionId,
    choiceId: item.choiceId,
    name: item.optionName,
    value: item.optionValue,
    price: item.optionPrice || 0,
    quantity: resolvePerDayOptionQuantity({
      isPerDay: !!item.perDay,
      rentalDays: perDayDays,
      requestedQuantity: item.quantity,
    }),
    ...(item.perDay ? { perDay: true } : {}),
    // Include new option type fields
    optionType: item.optionType,
    inputValue: item.inputValue,
    pendingFiles: item.pendingFiles,
  }));

  // Extract available branches from product
  const availableBranches = product.branches?.map((branch) => ({
    id: branch.id,
    name: branch.name,
  })) || [];

  // Extract enabled usage methods from product
  const usageMethods = product.usage_methods
    ?.filter((m) => m.enabled)
    .map((m) => ({
      method_type: m.method_type as "pickup" | "delivery" | "shipping",
      description: m.description || undefined,
      pickup_branch_id: m.pickup_branch_id || undefined,
      return_branch_id: m.return_branch_id || undefined,
    })) || [];

  // Extract input/file options with full details for checkout page entry
  const inputFileOptions: ProductOption[] = (product.options || [])
    .filter((opt) => opt.option_type === "input" || opt.option_type === "file")
    .map((opt) => ({
      id: opt.id,
      product_id: opt.product_id,
      option_type: opt.option_type,
      name: opt.name,
      price_adjustment: opt.price_adjustment,
      is_required: opt.is_required,
      display_order: opt.display_order,
      // input type fields
      input_placeholder: opt.input_placeholder,
      input_max_length: opt.input_max_length,
      // file type fields
      file_allowed_extensions: opt.file_allowed_extensions,
      file_max_size_mb: opt.file_max_size_mb,
      file_max_count: opt.file_max_count,
    }));

  return {
    product: {
      id: product.id,
      name: product.name,
      description: product.name,
      image: product.images?.[0]?.image_url || "/placeholder.svg",
      partnerId: product.partner_id,
      partnerName: product.partner?.name,
      partnerCountryCode: product.partner?.country_code ?? null,
      currency: sourceCurrency,
    },
    rental: rentals[0] || null,
    rentals,
    options,
    cancelable: true,
    availableBranches,
    usageMethods,
    inputFileOptions,
  };
}

/**
 * Result of saveAndNavigateToCheckout operation
 */
export type SaveAndNavigateResult =
  | { success: true }
  | { success: false; error: Error; errorType: "storage" | "unknown" };

/**
 * Save checkout data to sessionStorage + IndexedDB and navigate to checkout page
 *
 * Uses hybrid storage:
 * - sessionStorage: JSON-serializable data (tab-isolated, auto-cleanup)
 * - IndexedDB: File objects (cannot be JSON serialized)
 *
 * @returns Result object indicating success or failure with error details
 */
export async function saveAndNavigateToCheckout(
  checkoutData: CheckoutData,
  router: { push: (url: string) => void },
  locale: string
): Promise<SaveAndNavigateResult> {
  // Debug logging
  if (process.env.NODE_ENV === "development") {
    console.log("=== Checkout Data ===");
    console.log("Rentals:", checkoutData.rentals.map((r) => ({
      pickupBranchId: r.pickupBranchId,
      pickupMethodType: r.pickupMethodType,
      returnBranchId: r.returnBranchId,
      returnMethodType: r.returnMethodType,
    })));
    console.log("Options with files:", checkoutData.options.filter(o => o.pendingFiles?.length));
  }

  try {
    // Import checkoutStorage dynamically to avoid SSR issues
    const { saveFullCheckoutData } = await import("./checkoutStorage");

    // Save to sessionStorage + IndexedDB (handles File objects properly)
    // This can throw if IndexedDB is blocked (private browsing) or quota exceeded
    const sessionStorageSaved = await saveFullCheckoutData(checkoutData);

    if (!sessionStorageSaved) {
      // sessionStorage failed but IndexedDB (files) succeeded
      // Continue to checkout - files are saved, just metadata might be incomplete
      console.warn("sessionStorage save failed, but files were saved. Continuing to checkout.");
    }

    // Navigate to checkout with locale
    router.push(`/${locale}/checkout`);
    return { success: true };
  } catch (error) {
    // IndexedDB or critical failure - user's files were not saved
    console.error("Failed to save checkout data:", error);
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error)),
      errorType: "storage",
    };
  }
}
